/**
 * Lazy SwapKit client wrapper.
 *
 * The real implementation in `./client.impl.js` synchronously imports
 * the entire SwapKit / Doritokit dependency tree (Doritokit SDK,
 * @doritokit/wallet-keystore, secureKeystoreWallet → @radixdlt
 * toolkit, every plugin, viem, ECPair, Solana web3.js etc.). Loading
 * any of those at the top of the React tree balloons the initial
 * sync entrypoint by ~30MB and is what was making the page hang for
 * 10–20s on staging before the first paint.
 *
 * SKClientProviderManager only ever calls these functions inside
 * async callbacks, so we expose async wrappers that pull in the
 * heavy chunk on first use. The webpack chunk name keeps the network
 * trace readable.
 *
 * Adding a new export? Mirror it here and inside `client.impl.js`.
 */

let implPromise = null;

const loadImpl = () => {
	if (!implPromise) {
		implPromise = import(/* webpackChunkName: "swapkit-client-impl" */ './client.impl.js');
	}
	return implPromise;
};

export const createSwapKitClient = async (...args) => {
	const mod = await loadImpl();
	return mod.createSwapKitClient(...args);
};

export const getOrCreateClient = async (...args) => {
	const mod = await loadImpl();
	return mod.getOrCreateClient(...args);
};

export const connectWallet = async (...args) => {
	const mod = await loadImpl();
	return mod.connectWallet(...args);
};

/**
 * Force-load the SwapKit implementation chunk in the background.
 * Call this when the user has opened a wallet-related app (Money
 * Manager, Send wizard, etc.) so the chunk is warm by the time they
 * trigger an action. Safe to call multiple times — the dynamic
 * import is memoised.
 */
export const preloadSwapKitClient = () => {
	loadImpl().catch(() => { /* ignore — first real call will surface the error */ });
};
